import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import { movies } from '@/mocks/movies';
import { characters } from '@/mocks/characters';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function Character() {
  const { id } = useParams();
  const character = characters.find((c) => c.id === id);
  const movie = movies.find((m) => m.id === character?.movieId);

  usePageMeta(
    character ? `${character.name} - 角色介绍 - 玉女心经2` : '角色详情 - 玉女心经2',
    character ? `${character.name}（${character.actor} 饰）角色背景、性格分析与相关剧情点介绍。` : ''
  );

  if (!character) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
          <i className="ri-user-line text-3xl"></i>
        </span>
        <h1 className="mt-4 font-heading text-xl font-bold text-foreground-900">未找到该角色</h1>
        <p className="mt-2 text-sm text-foreground-600">它可能已被移除，或者链接有误。</p>
        <Link
          to="/category"
          className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 hover:bg-primary-600 cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i> 返回影视分类
        </Link>
      </div>
    );
  }

  const others = characters.filter((c) => c.movieId === character.movieId && c.id !== character.id);

  return (
    <div className="w-full bg-background-50 px-4 md:px-8 py-8 md:py-12">
      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: '影视分类', to: '/category' },
          ...(movie ? [{ label: movie.title, to: `/detail/${movie.id}` }] : []),
          { label: character.name },
        ]}
      />

      {/* 角色头部 */}
      <section className="mt-6 overflow-hidden rounded-lg border border-background-200/70 bg-background-100">
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr]">
          <div className="relative aspect-square w-full max-h-[420px] overflow-hidden bg-background-200 md:aspect-auto md:h-full">
            <img
              src={character.avatar}
              alt={`${character.name} 角色头像`}
              title={`${character.name} · ${character.actor} 饰`}
              className="h-full w-full object-top"
            />
            <span className="absolute left-4 top-4 rounded-md bg-accent-500 px-3 py-1 font-label text-xs text-background-50">
              {character.roleTag}
            </span>
          </div>

          <div className="flex flex-col justify-center p-6 md:p-8">
            <h1 className="font-heading text-3xl font-black text-foreground-950 md:text-4xl">{character.name}</h1>
            <p className="mt-2 text-sm text-foreground-600">由演员 <span className="text-foreground-900">{character.actor}</span> 饰演</p>

            {movie && (
              <Link
                to={`/detail/${movie.id}`}
                className="mt-6 flex w-fit items-center gap-3 rounded-lg border border-background-200/70 bg-background-50 p-3 transition-colors hover:border-primary-300 cursor-pointer"
              >
                <div className="h-16 w-11 shrink-0 overflow-hidden rounded-md bg-background-200">
                  <img src={movie.poster} alt={`${movie.title} 海报`} title={movie.title} className="h-full w-full object-top" />
                </div>
                <div className="pr-3">
                  <p className="text-[11px] text-foreground-500">所属影视作品</p>
                  <p className="font-heading text-sm font-bold text-foreground-950">{movie.title}</p>
                  <p className="mt-0.5 text-xs text-primary-700">查看作品详情 <i className="ri-arrow-right-s-line"></i></p>
                </div>
              </Link>
            )}
          </div>
        </div>
      </section>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-8">
          <section className="rounded-lg border border-background-200/70 bg-background-100 p-6 md:p-7">
            <h2 className="flex items-center gap-2 font-heading text-xl font-black text-foreground-950">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                <i className="ri-book-2-line"></i>
              </span>
              角色背景介绍
            </h2>
            <p className="mt-4 text-sm leading-8 text-foreground-700">{character.background}</p>
          </section>

          <section className="rounded-lg border border-background-200/70 bg-background-100 p-6 md:p-7">
            <h2 className="flex items-center gap-2 font-heading text-xl font-black text-foreground-950">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-heart-3-line"></i>
              </span>
              性格分析
            </h2>
            <p className="mt-4 text-sm leading-8 text-foreground-700">{character.personality}</p>
          </section>

          <section className="rounded-lg border border-background-200/70 bg-background-100 p-6 md:p-7">
            <h2 className="flex items-center gap-2 font-heading text-xl font-black text-foreground-950">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                <i className="ri-bookmark-3-line"></i>
              </span>
              相关剧情点
            </h2>
            <ol className="mt-5 space-y-4">
              {character.plotPoints.map((point, i) => (
                <li key={i} className="flex gap-4">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-500 font-label text-xs font-bold text-background-50">
                    {i + 1}
                  </span>
                  <p className="pt-0.5 text-sm leading-7 text-foreground-700">{point}</p>
                </li>
              ))}
            </ol>
          </section>
        </div>

        {/* 侧栏：同作品其他角色 */}
        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-24 space-y-4">
            <div className="rounded-lg border border-background-200/70 bg-background-100 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-team-line text-primary-600"></i> 同作品其他角色
              </h3>
              {others.length > 0 ? (
                <ul className="mt-4 space-y-3">
                  {others.map((c) => (
                    <li key={c.id}>
                      <Link to={`/character/${c.id}`} className="group flex items-center gap-3 cursor-pointer">
                        <div className="h-12 w-12 shrink-0 overflow-hidden rounded-full bg-background-200">
                          <img src={c.avatar} alt={`${c.name} 头像`} title={c.name} className="h-full w-full object-top" />
                        </div>
                        <div className="min-w-0">
                          <p className="truncate font-label text-sm font-medium text-foreground-900 group-hover:text-primary-700">{c.name}</p>
                          <p className="truncate text-xs text-foreground-500">{c.roleTag} · {c.actor}</p>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-4 text-sm text-foreground-600">暂无其他角色资料。</p>
              )}
            </div>

            {movie && (
              <Link
                to={`/detail/${movie.id}`}
                className="flex items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-3 font-label text-sm text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i> 返回《{movie.title}》详情
              </Link>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}